import { gql, mutate, watchQuery } from '@/services/apollo'
import { saveAs } from 'file-saver'
import { authenticatedPost } from '@/shared/fetch'

const FRAGMENTS = {
  MONETARY_AMOUNT: gql`
    fragment monetaryAmount on MonetaryAmount {
      value
      currency
    }
  `
}

const GET_CURRENCIES = gql`
  query Currencies{
    currencies
  }
`

const GET_REVENUE_REPORT = gql`
  query RevenueReport($input: RevenueReportInput!){
    revenueReport(input: $input){
      revenues {
        gameName
        winnings {
          ...monetaryAmount
        }
        stake {
          ...monetaryAmount
        }
        ggr {
          ...monetaryAmount
        }
        rtp
        date
      }
      sum {
        winnings {
          ...monetaryAmount
        }
        stake {
          ...monetaryAmount
        }
        ggr {
          ...monetaryAmount
        }
        rtp
      }
    }
  }

  ${FRAGMENTS.MONETARY_AMOUNT}
`

const GET_GAME_PERFORMANCE_REVENUE_REPORT_PAGE = gql`
  query getGamePerformanceRevenueReportPage($input: GamePerformanceReportPageInput!) {
    gamePerformanceReportPage(input: $input) {
      gamePerformanceReports {
        id
        gameId
        currency
        totalWagered
        totalWon
        rtp
        firstPlay
        gameCycles
        wageredInOpen
        wageredInRefunded
        theoreticalRtp
        wageredInGamble
        wonInGamble
        gameCyclesGambled
        gameCyclesGambledAndWon
      }
      totalCount
    }
  }
`

const GET_CASINO_PERFORMANCE_REVENUE_REPORT_PAGE = gql`
  query getCasinoPerformanceRevenueReportPage($input: CasinoPerformanceReportPageInput!) {
    casinoPerformanceReportPage(input: $input) {
      casinoPerformanceReports {
        casinoId
        casinoName
        currency
        won
        wagered
        ggr
        gameCycles
        rtp
      }
      totalCount
    }
  }
`


const GET_SIGNIFICANT_EVENT_PAGE = gql`
  query getSignificantEventPage($input: SignificantEventsInput!) {
    significantEventPage(input: $input) {
      significantEvents {
        id
        username
        description
        createdAt
        happenedAt
      }
      totalCount
    }
  }
`

const CREATE_SIGNIFICANT_EVENT = gql`
  mutation createSignificantEvent($input: CreateSignificantEventInput!) {
    createSignificantEvent(input: $input) {
      id
      username
      description
      createdAt
      happenedAt
    }
  }
`

const GET_CPV_CHECKS = gql`
  query cpvCheckPage($input: CPVChecksInput!) {
    cpvCheckPage(input: $input) {
      cpvChecks {
        id
        component
        date
        hash
        hashAllowList
        result
      }
      totalCount
    }
  }
`

const TRIGGER_CPV_CHECK = gql`
  mutation triggerCPVCheck {
    triggerCPVCheck
  }
`

const GET_COMPONENT_HASH_PAGE = gql`
  query getComponentHashPage($input: ComponentHashInput!) {
    componentHashPage(input: $input) {
      hashes,
      totalCount
    }
  }
`

const ADD_COMPONENT_HASH = gql`
  mutation addComponentHash($input: AddComponentHashInput!) {
    addComponentHash(input: $input)
  }
`

const REMOVE_COMPONENT_HASH = gql`
  mutation removeComponentHash($input: DeleteComponentHashInput!) {
    deleteComponentHash(input: $input)
  }
`

const saveFile = async request => {
  const blob = await request.blob()
  const header = request.headers.get('Content-Disposition') || ''
  const match = header.match(/filename=(.+)/)
  const filename = decodeURIComponent(match[1])
  return saveAs(blob, filename)
}

export default {
  getCurrencies () {
    return watchQuery({ query: GET_CURRENCIES })
  },
  getGamePerformanceRevenueReportPage (variables) {
    return watchQuery({ query: GET_GAME_PERFORMANCE_REVENUE_REPORT_PAGE, variables })
  },
  getCasinoPerformanceRevenueReportPage (variables) {
    return watchQuery({ query: GET_CASINO_PERFORMANCE_REVENUE_REPORT_PAGE, variables })
  },
  getSignificantEventPage (variables) {
    return watchQuery({ query: GET_SIGNIFICANT_EVENT_PAGE, variables})
  },
  createSignificantEvent (input) {
    return mutate({ mutation: CREATE_SIGNIFICANT_EVENT, variables: { input }})
  },
  getCPVChecks (variables) {
    return watchQuery({ query: GET_CPV_CHECKS, variables})
  },
  triggerCPVCheck (variables) {
    return mutate({ mutation: TRIGGER_CPV_CHECK, variables })
  },
  getComponentHashPage (variables) {
    return watchQuery({ query: GET_COMPONENT_HASH_PAGE, variables })
  },
  addComponentHash (input) {
    return mutate({ mutation: ADD_COMPONENT_HASH, variables: { input } })
  },
  removeComponentHash (input) {
    return mutate({ mutation: REMOVE_COMPONENT_HASH, variables: { input }, refetchQueries: () => ['getComponentHashPage']})
  },
  downloadGamePerformanceReport (input) {
    return authenticatedPost('/game-performance-reports/download', input)
      .then(saveFile)
  },
  exportGamePerformanceToExcel (input) {
    return authenticatedPost('/game-performance/export', input)
      .then(saveFile)
  },
  exportCasinoPerformanceToExcel (input) {
    return authenticatedPost('/casino-performance/export', input)
      .then(saveFile)
  },
  downloadAuditLog (input) {
    return authenticatedPost('/audit-log/download', input)
      .then(saveFile)
  },
  downloadCPVReport (input) {
    return authenticatedPost('/cpv-report/download', input)
      .then(saveFile)
  },
  downloadFailedBookingsReport (input) {
    return authenticatedPost('/failed-bookings-report/download', input)
      .then(saveFile)
  },
  downloadSignificantEventsReport (input) {
    return authenticatedPost('/significant-events-report/download', input)
      .then(saveFile)
  },
  downloadGamePlayHistory (input) {
    return authenticatedPost('/game-play-history/download', input)
      .then(saveFile)
  },
}
